// SocialCommentsTool.js — Coursera-branded “Social Comments: Simple Cut & Paste”
// Single file (no Tailwind). Requires: npm i xlsx
// Route suggestion: #/tools/social-comments

import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

/* ===================== Brand tokens (Coursera + dark UI) ===================== */
const TOKENS = {
  c: {
    blue: "#2A73CC",
    navy: "#0E2B57",
    ink: "#0B1A3C",
    ink80: "#0B1A3CCC",
    line: "#E6EDF2",
    bg: "#F4F6FA",
    card: "#FFFFFF",
  },
  dark: {
    bg: "#0F172A",
    panel: "#0B162A",
    line: "#20314A",
    ink: "#EAF2FF",
    ink70: "#EAF2FFB3",
  },
  r: { sm: 8, md: 12, lg: 16 },
  sh: {
    xs: "0 1px 2px rgba(0,0,0,.08)",
    sm: "0 4px 12px rgba(0,0,0,.12)",
    md: "0 8px 28px rgba(0,0,0,.14)",
  },
};

const LOGO_FAIRWAY =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/logos/Final%20Logo_400.png";

const LOGO_COURSERA_SVG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_COURSERA_PNG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";

/* ============================= Heuristics/Helpers ============================ */

const COMPANY_HINTS = [
  " inc",
  " llc",
  " ltd",
  " group",
  " corp",
  " corporation",
  " technologies",
  " technology",
  " systems",
  " solutions",
  " partners",
  " networks",
  " consulting",
  " media",
  " marketing",
  " insights",
  " advisors",
  " ventures",
  " capital",
  " holdings",
  " labs",
  " studio",
  " software",
  " hardware",
];

const defaultMarkers = ["like", "love", "celebrate", "insightful", "support"];

const normalizeText = (s = "") =>
  s
    .normalize("NFKC")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();

function cleanName(s) {
  let t = s;
  t = t.replace(/^View\s+(.+?)’s profile.*$/i, "$1");
  t = t.replace(/^View\s+(.+?)'s profile.*$/i, "$1");
  t = t.replace(/[\|•·]+.*$/, "");
  t = t.replace(/\s{2,}/g, " ").trim();
  return t;
}

const extractConnection = (s) =>
  (s.match(/\b(1st|2nd|3rd\+?)\b/) || ["", ""])[1] || "";

function looksLikeCompany(fragment) {
  const f = fragment.toLowerCase().trim();
  if (COMPANY_HINTS.some((h) => f.includes(h))) return true;
  const words = f.split(/\s+|\|/).filter(Boolean);
  if (!words.length) return false;
  const score =
    words.filter((w) => /^[A-Z][A-Za-z]*$/.test(w) || /^[A-Z0-9-]+$/.test(w))
      .length / words.length;
  if (score > 0.6) {
    if (
      /(chief |director|manager|president|founder|advisor|attorney|engineer|marketing|sales|revenue|demand|partner|account|customer|success|operations)/.test(
        f
      )
    )
      return false;
    return true;
  }
  return false;
}

/* -------- URL helpers: unwrap linkedin safety/go and pick profile urls ------- */
function decodeMaybeWrappedLinkedIn(href) {
  try {
    const u = new URL(href, "https://dummy.base/");
    // unwrap linkedin safety/go?url=<encoded>
    if (
      /linkedin\.com\/safety\/go/i.test(u.hostname + u.pathname) &&
      u.searchParams.get("url")
    ) {
      return decodeURIComponent(u.searchParams.get("url"));
    }
    return href;
  } catch {
    return href;
  }
}

function isLinkedInProfileUrl(href) {
  const h = href.toLowerCase();
  return (
    /linkedin\.com\/in\//i.test(h) ||
    /m\.linkedin\.com\/in\//i.test(h) ||
    /lnkd\.in\//i.test(h)
  );
}

/* Social profile extractor (works for common social URLs; prefers /in/ slugs) */
function findProfileInBlock(lines) {
  const text = lines.join(" ");
  // Prefer /in/ profile slugs
  const inMatch =
    text.match(
      /(?:https?:\/\/)?(?:[a-z]+\.)?linkedin\.com\/in\/([A-Za-z0-9\-_%\.]+)(?:[\/?#]\S*)?/i
    ) ||
    text.match(
      /(?:https?:\/\/)?m\.linkedin\.com\/in\/([A-Za-z0-9\-_%\.]+)(?:[\/?#]\S*)?/i
    );
  if (inMatch) {
    const handle = decodeURIComponent(inMatch[1]).split(/[/?#]/)[0];
    return {
      profile_handle: handle,
      profile_url: `https://www.linkedin.com/in/${handle}/`,
    };
  }
  const short = text.match(/(?:https?:\/\/)?lnkd\.in\/[A-Za-z0-9_-]+/i);
  if (short) {
    const url = short[0].startsWith("http") ? short[0] : "https://" + short[0];
    return { profile_handle: "", profile_url: url };
  }
  return { profile_handle: "", profile_url: "" };
}

function splitTitleCompany(headline) {
  const h = normalizeText(headline);
  const m = h.match(/^(?<title>.+?)\s+(?:at|@)\s+(?<company>.+)$/i);
  if (m?.groups)
    return [m.groups.title.replace(/[ |,-]+$/g, ""), m.groups.company.trim()];
  const parts = h
    .split("|")
    .map((p) => p.replace(/^[ |\-\u2013]+|[ |\-\u2013]+$/g, ""))
    .filter(Boolean);
  if (parts.length >= 2 && looksLikeCompany(parts[parts.length - 1])) {
    return [parts.slice(0, -1).join(" | "), parts[parts.length - 1]];
  }
  return [h, ""];
}

/* Parse “reaction blocks” pasted from social */
function parseBlocks(text, markers = defaultMarkers) {
  const lines = text.split(/\r?\n/).map((l) => normalizeText(l));
  const EMOTION = new Set(
    (markers || []).map((m) => (m || "").trim().toLowerCase()).filter(Boolean)
  );
  const entries = [];
  let i = 0;

  // --- local helpers ---
  const isProfileURL = (s = "") =>
    /^(?:https?:\/\/)?(?:[a-z]+\.)?linkedin\.com\/in\/\S+$/i.test(s.trim()) ||
    /^(?:https?:\/\/)?lnkd\.in\/\S+$/i.test(s.trim());

  const connectionToken = (s = "") => {
    const m = s.match(/\b(1st|2nd|3rd\+?)\b/i);
    return m ? m[1] : "";
  };

  const isConnectionOnlyLine = (s = "") => {
    const t = s.trim();
    if (!t) return false;
    if (/degree connection/i.test(t)) return true;
    if (/^(?:[-•·]\s*)?(?:1st|2nd|3rd\+?)$/i.test(t)) return true;
    return false;
  };

  while (i < lines.length) {
    const line = (lines[i] || "").trim().toLowerCase();
    if (!EMOTION.has(line)) {
      i++;
      continue;
    }

    // collect this block until the next marker
    i += 1;
    const blockLines = [];
    while (
      i < lines.length &&
      !EMOTION.has((lines[i] || "").trim().toLowerCase())
    ) {
      blockLines.push(lines[i]);
      i++;
    }

    // walk the block
    let b = 0;
    const nextNonEmpty = () => {
      while (b < blockLines.length && !blockLines[b]) b++;
      return b < blockLines.length ? blockLines[b] : "";
    };

    // 1) Name (first non-empty)
    let name = "";
    if (nextNonEmpty()) {
      name = cleanName(blockLines[b]);
      b++;
    }

    // 2) Capture any connection degree if it appears immediately after “View … profile”
    let connection = "";
    if (
      b < blockLines.length &&
      /^view .+ profile/i.test(blockLines[b] || "")
    ) {
      connection = connectionToken(blockLines[b] || "") || connection;
      b++;
    }
    if (
      b < blockLines.length &&
      /\b(1st|2nd|3rd\+?)\b/.test(blockLines[b] || "")
    ) {
      connection = connectionToken(blockLines[b] || "") || connection;
      b++;
    }

    // 3) HEADLINE SELECTION
    let headline = "";
    for (let j = b; j < blockLines.length; j++) {
      const t = blockLines[j] || "";
      if (!t.trim()) continue; // skip blanks
      if (isProfileURL(t)) continue; // skip profile URLs
      if (isConnectionOnlyLine(t)) {
        connection = connection || connectionToken(t);
        continue;
      }
      if (/^view .+ profile/i.test(t)) {
        connection = connection || connectionToken(t);
        continue;
      }
      headline = t;
      break;
    }

    // 4) Title / Company
    const [title_role, company] = splitTitleCompany(headline);

    // 5) Profile (handle/url) anywhere in the block
    const { profile_handle, profile_url } = findProfileInBlock(blockLines);

    // 6) clean name (remove accidental marker prefixes)
    name = name.replace(/^(like|love|celebrate|insightful|support)\s+/i, "");

    if (name) {
      entries.push({
        name,
        title_role,
        company,
        connection,
        profile_handle,
        profile_url,
      });
    }
  }

  // dedupe
  const seen = new Set();
  const unique = [];
  for (const e of entries) {
    const key = [
      (e.name || "").toLowerCase(),
      (e.title_role || "").toLowerCase(),
      (e.company || "").toLowerCase(),
    ].join("||");
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(e);
    }
  }
  return unique;
}

/* ================================ Export utils =============================== */

function toRowsForExport(rows) {
  return rows.map((r) => {
    const tokens = (r.name || "").trim().split(/\s+/);
    const first_name =
      tokens.length > 1 ? tokens.slice(0, -1).join(" ") : r.name || "";
    const last_name = tokens.length > 1 ? tokens[tokens.length - 1] : "";
    return {
      full_name: r.name || "",
      first_name,
      last_name,
      title_role: r.title_role || "",
      company: r.company || "",
      connection: r.connection || "",
      profile_handle: r.profile_handle || "",
      profile_url: r.profile_url || "",
      email: "",
      domain: "",
    };
  });
}

function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) =>
    `"${String(v ?? "")
      .replace(/"/g, '""')
      .replace(/\r?\n/g, " ")}"`;
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function downloadXLSX(filename, rows) {
  if (!rows.length) return;
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "contacts");
  XLSX.writeFile(wb, filename);
}

/* ===================== Paste HTML → inject profile URLs ===================== */

function extractLinkedInUrlsFromHTML(html) {
  const urls = [];
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const anchors = Array.from(doc.querySelectorAll("a[href]"));
    for (const a of anchors) {
      let href = a.getAttribute("href") || "";
      href = decodeMaybeWrappedLinkedIn(href);
      if (isLinkedInProfileUrl(href)) urls.push(href);
    }
  } catch {
    // ignore
  }
  return urls;
}

function injectUrlsIntoPlainText(plain, urls) {
  if (!urls.length) return plain;
  const lines = plain.split(/\r?\n/);
  let idx = 0;
  for (let i = 0; i < lines.length && idx < urls.length; i++) {
    if (/^view .+ profile/i.test(lines[i])) {
      const insertAt = i + 1;
      if (!/(https?:\/\/|lnkd\.in\/)/i.test(lines[insertAt] || "")) {
        lines.splice(insertAt, 0, urls[idx]);
        idx++;
        i++; 
      }
    }
  }
  while (idx < urls.length) {
    lines.push(urls[idx]);
    idx++;
  }
  return lines.join("\n");
}

/* =================================== UI ==================================== */

export default function SocialCommentsTool() {
  const [text, setText] = useState("");
  const [markers, setMarkers] = useState(defaultMarkers.join(", "));
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("Waiting for input");
  const taRef = useRef(null);

  const services = useMemo(
    () => [
      "Paste social reactions → structured contacts",
      "Role/title parsing with company detection",
      "Profile handle & URL capture (when present)",
      "Connection degree capture (1st/2nd/3rd+)",
      "CSV/XLSX exports with clean headers",
      "PII-safe: emails not required to parse",
    ],
    []
  );

  const parsedCount = rows.length;
  const exportRows = toRowsForExport(rows);

  function handleConvert() {
    const list = parseBlocks(
      text,
      markers
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean)
    );
    setRows(list);
    setStatus(
      list.length
        ? `Parsed ${list.length} ${list.length === 1 ? "entry" : "entries"}`
        : "No entries detected"
    );
  }

  function handleSample() {
    const SAMPLE = `like
John Doe
View John Doe’s profile • 2nd
VP, Revenue Operations at ACME TECHNOLOGIES
https://www.linkedin.com/in/john-doe-1234/

love
Jane Roe
View Jane Roe’s profile · 1st
Head of Marketing | ROCKET LABS
https://lnkd.in/dAbCdEf

celebrate
Avery Smith
View Avery Smith's profile
Director, Partnerships @ NORTHSTAR GROUP`;
    setText(SAMPLE);
    setStatus("Sample inserted");
  }

  function handleClear() {
    setText("");
    setRows([]);
    setStatus("Cleared");
    taRef.current?.focus();
  }

  function handlePaste(e) {
    try {
      const html = e.clipboardData?.getData("text/html") || "";
      const plain = e.clipboardData?.getData("text/plain") || "";
      if (!html) return; 
      const urls = extractLinkedInUrlsFromHTML(html);
      if (!urls.length) return; 
      e.preventDefault();
      const enhanced = injectUrlsIntoPlainText(plain, urls);
      const el = taRef.current;
      if (!el) {
        setText((prev) => (prev ? prev + "\n" + enhanced : enhanced));
        return;
      }
      const {
        selectionStart = el.value.length,
        selectionEnd = el.value.length,
      } = el;
      const before = el.value.slice(0, selectionStart);
      const after = el.value.slice(selectionEnd);
      const next = before + enhanced + after;
      setText(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = before.length + enhanced.length;
        el.setSelectionRange(pos, pos);
      });
    } catch {
      // ignore
    }
  }

  return (
    <div style={{ background: TOKENS.c.bg, minHeight: "100vh" }}>
      <style>{`
        :root{
          --ink:${TOKENS.c.ink}; --ink80:${TOKENS.c.ink80}; --line:${TOKENS.c.line};
          --bg:${TOKENS.c.bg}; --card:${TOKENS.c.card}; --blue:${TOKENS.c.blue}; --navy:${TOKENS.c.navy};
          --d-bg:${TOKENS.dark.bg}; --d-panel:${TOKENS.dark.panel}; --d-line:${TOKENS.dark.line};
          --d-ink:${TOKENS.dark.ink}; --d-ink70:${TOKENS.dark.ink70};
        }
        .wrap{max-width:1120px;margin:0 auto;padding:28px 20px;}
        .hero{position:relative;border-radius:16px;padding:24px;background:
          radial-gradient(1200px 420px at 120% -30%, rgba(255,255,255,.16), transparent 60%),
          linear-gradient(180deg, var(--blue) 0%, #1f62b2 100%);
          color:#fff; box-shadow:${TOKENS.sh.md}}
        .hero__brand{display:flex;align-items:center;gap:10px}
        .hero__title{margin:4px 0 6px;font-weight:800;font-size:28px;line-height:34px}
        .hero__sub{margin:0;opacity:.95}
        .badge{display:inline-flex;gap:8px;align-items:center;margin-left:auto;background:rgba(255,255,255,.12);
          border:1px solid rgba(255,255,255,.35);border-radius:999px;padding:6px 10px;font-weight:700}
        .badge img{height:18px}

        .grid{display:grid;grid-template-columns:1fr;gap:16px;margin-top:16px}
        .card{background:var(--card);border:1px solid var(--line);border-radius:16px;box-shadow:${TOKENS.sh.sm};padding:16px}
        .label{display:block;color:var(--ink);font-weight:700;font-size:12px;margin-bottom:6px}
        .hint{color:#475569;font-size:12px}
        .txt{width:100%;height:240px;border:1px solid var(--line);border-radius:12px;padding:12px 14px;resize:vertical}
        .toolbar{display:flex;gap:10px;flex-wrap:wrap;align-items:end;margin-top:10px}
        .input{border:1px solid var(--line);border-radius:10px;padding:8px 10px;min-width:280px}
        .btn{border:1px solid var(--line);background:#fff;color:var(--navy);padding:8px 12px;border-radius:12px;
          font-weight:700;cursor:pointer;box-shadow:${TOKENS.sh.xs}}
        .btn--pri{background:var(--blue);border-color:transparent;color:#fff}
        .pill{display:inline-flex;align-items:center;gap:8px;background:#F0F6FF;border:1px solid #E0EBFF;border-radius:999px;padding:4px 10px;font-weight:700;color:var(--navy)}

        table.tbl{width:100%;border-collapse:separate;border-spacing:0;border:1px solid var(--line);border-radius:12px;overflow:hidden}
        table.tbl thead th{background:#F8FAFE;border-bottom:1px solid var(--line);padding:10px 12px;text-align:left;font-weight:700}
        table.tbl tbody td{padding:10px 12px;border-bottom:1px solid var(--line)}
        table.tbl tbody tr:last-child td{border-bottom:none}
        a.link{color:var(--blue);text-decoration:none}
        a.link:hover{text-decoration:underline}
        .caps{background:var(--d-panel);color:var(--d-ink);border:1px solid var(--d-line);border-radius:12px;padding:10px 12px}
        .caps:before{content:"✓ ";color:#8FE1A2;font-weight:900}
      `}</style>

      <div className="wrap">
        {/* Hero */}
        <div className="hero">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="hero__brand">
              <picture>
                <source srcSet={LOGO_COURSERA_SVG} type="image/svg+xml" />
                <img src={LOGO_COURSERA_PNG} alt="Coursera" height="18" />
              </picture>
              <span style={{ opacity: 0.9, fontWeight: 700 }}>
                for Business
              </span>
            </div>
            <div style={{ flex: 1 }} />
            <div className="badge" title="Powered by Fairway">
              <img src={LOGO_FAIRWAY} alt="" />
              <span>Powered by Fairway</span>
            </div>
          </div>
          <h1 className="hero__title">Social Comments: Simple Cut & Paste</h1>
          <p className="hero__sub">
            Paste reaction blocks from social posts → get a clean, deduped
            contacts table with profile links and exports.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
              gap: 10,
              marginTop: 14,
            }}
          >
            {services.map((s, i) => (
              <div key={i} className="caps">
                {s}
              </div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="card">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:12}}>
            <div style={{flex:1}}>
              <label className="label" htmlFor="markers">
                Reaction markers
              </label>
              <input
                id="markers"
                className="input"
                placeholder="like, love, celebrate, insightful, support"
                value={markers}
                onChange={(e) => setMarkers(e.target.value)}
                style={{width:'100%'}}
              />
            </div>
            
            {/* NEW BUTTON TO PLAYBOOK */}
            <a 
              href="#/tools/social-comments-playbook"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 700, color: '#2563EB',
                textDecoration: 'none', background: '#EFF6FF',
                border: '1px solid #DBEAFE', padding: '10px 14px', borderRadius: 12,
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>📘 How to use this (Playbook)</span>
              <span>→</span>
            </a>
          </div>

          <div className="toolbar">
            <button className="btn" onClick={handleSample}>
              Use sample
            </button>
            <button className="btn" onClick={handleClear}>
              Clear
            </button>
            <button className="btn btn--pri" onClick={handleConvert}>
              Convert
            </button>
            <div
              style={{ marginLeft: "auto" }}
              className="pill"
              aria-live="polite"
            >
              {rows.length ? `Parsed ${rows.length}` : "Waiting for input"}
            </div>
          </div>
        </div>

        {/* Paste area */}
        <div className="card">
          <label htmlFor="paste" className="label">
            Paste
          </label>
          <textarea
            ref={taRef}
            id="paste"
            className="txt"
            placeholder="Paste your social text here…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={handlePaste}
          />
        </div>

        {/* Results */}
        <div className="card">
          {parsedCount === 0 ? (
            <div
              style={{
                border: "1px dashed var(--line)",
                borderRadius: 12,
                padding: 16,
                color: "#64748B",
              }}
            >
              Paste your text above and click <b>Convert</b>, or try{" "}
              <b>Use sample</b>.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <span className="pill">{`Parsed ${parsedCount}`}</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn"
                    onClick={() =>
                      downloadCSV(
                        `social_contacts_${new Date()
                          .toISOString()
                          .replace(/[:.]/g, "-")}.csv`,
                        exportRows
                      )
                    }
                  >
                    Export CSV
                  </button>
                  <button
                    className="btn"
                    onClick={() =>
                      downloadXLSX(
                        `social_contacts_${new Date()
                          .toISOString()
                          .replace(/[:.]/g, "-")}.xlsx`,
                        exportRows
                      )
                    }
                  >
                    Export XLSX
                  </button>
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 200 }}>Name</th>
                      <th style={{ minWidth: 240 }}>Title/Role</th>
                      <th style={{ minWidth: 220 }}>Company</th>
                      <th style={{ minWidth: 120 }}>Connection</th>
                      <th style={{ minWidth: 220 }}>Profile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td>{r.name}</td>
                        <td>{r.title_role}</td>
                        <td>{r.company}</td>
                        <td>{r.connection}</td>
                        <td>
                          {r.profile_url ? (
                            <a
                              className="link"
                              href={r.profile_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {r.profile_handle || r.profile_url}
                            </a>
                          ) : (
                            <span style={{ color: "#94A3B8" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="hint" style={{ marginTop: 8 }}>
                  Export schema includes: <code>profile_handle</code>,{" "}
                  <code>profile_url</code>, <code>full_name</code>,{" "}
                  <code>first_name</code>, <code>last_name</code>,{" "}
                  <code>title_role</code>, <code>company</code>,{" "}
                  <code>connection</code>, <code>email</code>,{" "}
                  <code>domain</code>.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
